import React, { useState, useRef, useEffect } from 'react';
import { useI18n, LANGUAGES } from '../i18n';

// Globe dropdown for choosing the interface language. Enabled languages switch
// the UI immediately; the rest are shown as "coming soon" (the agreed Top 10
// roadmap) so the direction is visible without pretending they're ready.
//
// `tone` lets the picker sit on a light header ('dark' text) or a teal/dark bar
// ('light' text).

interface Props { tone?: 'dark' | 'light'; }

export default function LanguagePicker({ tone = 'dark' }: Props) {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = LANGUAGES.find(l => l.code === lang) || LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const enabled = LANGUAGES.filter(l => l.enabled);
  const soon = LANGUAGES.filter(l => !l.enabled);
  const textColor = tone === 'light' ? 'white' : 'var(--teal)';

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-label={t('languagePicker.label')}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: textColor, fontWeight: 600, fontSize: 14, padding: '8px 8px', display: 'flex', alignItems: 'center', gap: 6 }}
      >
        <span aria-hidden>🌐</span>
        <span>{current.flag} {current.native}</span>
      </button>

      {open && (
        <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: 'var(--white)', borderRadius: 12, boxShadow: 'var(--shadow-lg)', padding: 8, minWidth: 220, zIndex: 200, maxHeight: '70vh', overflowY: 'auto', textAlign: 'left' }}>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: 'var(--text-secondary)', textTransform: 'uppercase', padding: '6px 10px 4px' }}>{t('languagePicker.availableNow')}</p>
          {enabled.map(l => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', background: l.code === lang ? 'var(--teal-light)' : 'none', border: 'none', cursor: 'pointer', padding: '10px 10px', borderRadius: 8, fontSize: 14, color: 'var(--charcoal)', fontWeight: l.code === lang ? 700 : 500 }}
            >
              <span aria-hidden style={{ fontSize: 16 }}>{l.flag}</span>
              <span>{l.native}</span>
              {l.code === lang && <span style={{ marginLeft: 'auto', color: 'var(--teal)', fontWeight: 700 }}>✓</span>}
            </button>
          ))}
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: 'var(--text-secondary)', textTransform: 'uppercase', padding: '10px 10px 4px' }}>{t('languagePicker.comingSoon')}</p>
          {soon.map(l => (
            <div key={l.code} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', fontSize: 14, color: 'var(--text-secondary)', opacity: 0.6 }}>
              <span aria-hidden style={{ fontSize: 16 }}>{l.flag}</span>
              <span>{l.native}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
